<template>
  <div class="page-container">
    <h2 class="page-title">优惠券管理</h2>

    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="优惠券名称">
          <el-input v-model="searchForm.name" placeholder="输入优惠券名称" clearable></el-input>
        </el-form-item>

        <el-form-item label="优惠券类型">
          <el-select v-model="searchForm.type_id" placeholder="全部类型" clearable style="width: 200px">
            <el-option v-for="item in couponTypes" :key="item.value" :label="item.label"
              :value="item.value"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="全部状态" clearable style="width: 200px">
            <el-option v-for="item in statusOptions" :key="item.value" :label="item.label"
              :value="item.value"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSearch" :loading="loading">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 操作按钮 -->
    <div class="action-bar">
      <el-button type="primary" icon="Plus" @click="handleCreate">新增优惠券</el-button>
    </div>

    <!-- 数据表格 -->
    <el-table :data="tableData" border stripe v-loading="loading">
      <el-table-column prop="name" label="名称" width="180"></el-table-column>
      <el-table-column prop="type_id" label="类型" width="120">
        <template #default="{ row }">
          <el-tag :type="typeTagMap[row.type_id]">
            {{ typeTextMap[row.type_id] }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="coupon_money" label="面值" width="120">
        <template #default="{ row }">
          ¥{{ row.coupon_money.toFixed(2) }}
        </template>
      </el-table-column>
      <el-table-column prop="money_limit" label="使用门槛" width="150">
        <template #default="{ row }">
          {{ row.money_limit > 0 ? `满${row.money_limit}元可用` : '无门槛' }}
        </template>
      </el-table-column>
      <el-table-column label="有效期" width="350">
        <template #default="{ row }">
          {{ formatDate(row.valid_bg_time) }} 至
          {{ formatDate(row.valid_end_time) }}
        </template>
      </el-table-column>
      <el-table-column prop="quota" label="库存" width="150">
        <template #default="{ row }">
          {{ row.quota - row.take_count }} / {{ row.quota }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="142">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'danger'">
            {{ statusTextMap[row.status] }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" fixed="right" width="200">
        <template #default="{ row }">
          <el-button type="primary" size="small" @click="handleEdit(row)">编辑</el-button>

          <el-button type="success" size="small" @click="showIssueDialog(row)">发行</el-button>

          <el-popconfirm title="确定要删除此优惠券吗？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button type="danger" size="small">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页组件 -->
    <div class="pagination">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange" @current-change="handlePageChange"></el-pagination>
    </div>


    <!-- 新增发行弹窗 -->
    <el-dialog v-model="dialogVisibleIssue" title="选择发放用户" width="800px">
      <div class="user-select-container">
        <!-- 用户搜索 -->
        <div class="search-user">
          <el-input v-model="userSearch" placeholder="输入用户名或手机号" clearable @input="handleSearchInput"
            @clear="handleUserSearch" @keyup.enter="handleUserSearch">
            <template #append>
              <el-button @click="handleUserSearch" :loading="userLoading"> <!-- 添加加载状态 -->
                <el-icon>
                  <Search />
                </el-icon>
              </el-button>
            </template>
          </el-input>
        </div>

        <!-- 用户列表 -->
        <el-table ref="userTableRef" :data="userList" height="400" @selection-change="handleSelectionChange">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="nickName" label="昵称" width="150">
            <template #default="{ row }">
              <div class="user-info">
                <el-avatar :size="30" :src="row.avatarUrl" />
                <span class="nickname">{{ row.nickName }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="real_name" label="姓名" width="120" />
          <el-table-column prop="phone_number" label="手机号" width="150" />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="row.status === 2 ? 'success' : 'info'">
                {{ row.status === 2 ? '已认证' : '未认证' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="注册时间" />
        </el-table>

        <!-- 用户分页 -->
        <div class="pagination">
          <el-pagination v-model:current-page="userPagination.page" v-model:page-size="userPagination.limit"
            :total="userPagination.total" layout="prev, pager, next" @current-change="fetchUsers" />
        </div>
      </div>

      <template #footer>
        <el-button @click="dialogVisibleIssue = false">取消</el-button>
        <el-button type="primary" :loading="issuing" @click="handleIssueConfirm">确认发放（{{ selectedUsers.length
        }}人）</el-button>
      </template>
    </el-dialog>

    <!-- 创建/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogType === 'create' ? '新增优惠券' : '编辑优惠券'" width="600px">
      <el-form ref="formRef" :model="formData" label-width="100px" :rules="formRules">
        <el-form-item label="优惠券名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入优惠券名称"></el-input>
        </el-form-item>

        <el-form-item label="优惠券类型" prop="type_id">
          <el-select v-model="formData.type_id" placeholder="请选择类型">
            <el-option v-for="item in couponTypes" :key="item.value" :label="item.label"
              :value="item.value"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="面值金额" prop="coupon_money">
          <el-input-number v-model="formData.coupon_money" :min="0" :precision="2"
            controls-position="right"></el-input-number>
        </el-form-item>

        <el-form-item label="使用门槛" prop="money_limit">
          <el-input-number v-model="formData.money_limit" :min="0" :precision="2" controls-position="right"><template
              #prefix>¥</template></el-input-number>
        </el-form-item>

        <el-form-item label="发放时间" prop="timeRange">
          <el-date-picker v-model="formData.timeRange" type="datetimerange" range-separator="至" start-placeholder="开始时间"
            end-placeholder="结束时间" value-format="YYYY-MM-DD HH:mm:ss"></el-date-picker>
        </el-form-item>

        <el-form-item label="有效期" prop="validRange">
          <el-date-picker v-model="formData.validRange" type="datetimerange" range-separator="至"
            start-placeholder="开始时间" end-placeholder="结束时间" value-format="YYYY-MM-DD HH:mm:ss"></el-date-picker>
        </el-form-item>

        <el-form-item label="发行数量" prop="quota">
          <el-input-number v-model="formData.quota" :min="1" :precision="0" controls-position="right"></el-input-number>
        </el-form-item>

        <el-form-item label="积分兑换" prop="is_integral_exchange">
          <el-switch v-model="formData.is_integral_exchange" active-value="1" inactive-value="0"></el-switch>
          <el-input-number v-if="formData.is_integral_exchange" v-model="formData.exchange_integral" :min="0"
            class="ml-10" controls-position="right"></el-input-number>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="formData.status">
            <el-radio :label="1">上架</el-radio>
            <el-radio :label="-1">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config'
// 新增状态变量
const userLoading = ref(false) // 用户列表加载状态
let searchTimer = null // 防抖计时器

// 输入事件处理（带防抖）
const handleSearchInput = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    handleUserSearch()
  }, 500) // 500毫秒防抖
}
// 用户信息
const userInfo = computed(() => {
  return JSON.parse(Cookies.get('user_info') || '{}')
})

// 表格数据
const tableData = ref([])
const loading = ref(false)
const dialogVisible = ref(false)
const submitting = ref(false)
const dialogType = ref('create')
const formRef = ref(null)

// 新增状态变量
const dialogVisibleIssue = ref(false)
const userList = ref([])
const selectedUsers = ref([])
const userSearch = ref('')
const issuing = ref(false)
const currentCoupon = ref(null)
const userTableRef = ref(null)
const userPagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})


// 分页参数
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 搜索条件
const searchForm = reactive({
  name: '',
  type_id: null,
  status: null
})

// 表单数据
const formData = reactive({
  id: null,
  name: '',
  type_id: 1,
  coupon_money: 0,
  money_limit: 0,
  timeRange: [],
  validRange: [],
  quota: 100,
  is_integral_exchange: 0,
  exchange_integral: 0,
  status: 1
})

// 验证规则
const formRules = {
  name: [{ required: true, message: '请输入优惠券名称', trigger: 'blur' }],
  type_id: [{ required: true, message: '请选择优惠券类型', trigger: 'change' }],
  coupon_money: [
    { required: true, message: '请输入面值金额', trigger: 'blur' },
    { type: 'number', min: 0, message: '金额不能小于0', trigger: 'blur' }
  ],
  timeRange: [
    { required: true, message: '请选择发放时间', trigger: 'change' }
  ],
  validRange: [
    { required: true, message: '请选择有效期', trigger: 'change' }
  ],
  quota: [
    { required: true, message: '请输入发行数量', trigger: 'blur' },
    { type: 'number', min: 1, message: '最少发行1张', trigger: 'blur' }
  ]
}

// 类型选项
const couponTypes = [
  { value: 1, label: '普通券' },
  { value: 2, label: '新人券' },
  { value: 3, label: '会员券' },
  { value: 4, label: '邀请券' }
]

const statusOptions = [
  { value: 1, label: '上架' },
  { value: -1, label: '下架' }
]

const typeTextMap = {
  1: '普通券',
  2: '新人券',
  3: '会员券',
  4: '邀请券'
}

const typeTagMap = {
  1: '',
  2: 'success',
  3: 'warning',
  4: 'danger'
}

const statusTextMap = {
  1: '上架中',
  '-1': '已下架'
}


// 显示发行弹窗
const showIssueDialog = (row) => {
  currentCoupon.value = row
  dialogVisibleIssue.value = true
  fetchUsers()
}

// 优化后的获取用户列表方法
const fetchUsers = async () => {
  try {
    const { data } = await axios.get(`${config.BASE_URL}/shop/api/users.php`, {
      params: {
        store_id: userInfo.value.id,
        page: userPagination.page,
        limit: userPagination.limit,
        search: userSearch.value.trim() // 去除前后空格
      }
    })

    if (data.code === 200) {
      userList.value = data.data.list.map(user => ({
        ...user,
        // 处理可能为null的字段
        nickName: user.nickName || '未设置昵称',
        real_name: user.real_name || '未实名',
        phone_number: user.phone_number || '未绑定手机'
      }))
      userPagination.total = data.data.pagination.total
    }
  } catch (error) {
    ElMessage.error('获取用户失败: ' + (error.response?.data?.message || error.message))
    userList.value = []
  }
}

// 处理用户选择
const handleSelectionChange = (selection) => {
  selectedUsers.value = selection.map(u => u.id)
}

// 优化后的搜索方法
const handleUserSearch = async () => {
  try {
    userLoading.value = true
    userPagination.page = 1 // 重置到第一页
    await fetchUsers()
  } finally {
    userLoading.value = false
  }
}

// 修改 handleIssueConfirm 方法
const handleIssueConfirm = async () => {
  try {
    if (selectedUsers.value.length === 0) {
      ElMessage.warning('请先选择要发放的用户')
      return
    }

    issuing.value = true
    
    // 调用真实接口
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/issue_coupon.php`, {
      coupon_id: currentCoupon.value.id,
      user_ids: selectedUsers.value
    })

    if (data.code === 200) {
      // 更新本地数据
      const index = tableData.value.findIndex(item => item.id === currentCoupon.value.id)
      if (index > -1) {
        tableData.value[index].take_count += selectedUsers.value.length
      }
      ElMessage.success(`成功发放给 ${selectedUsers.value.length} 位用户`)
      dialogVisibleIssue.value = false
      selectedUsers.value = [] // 清空已选用户
      userTableRef.value.clearSelection() // 清空表格选中状态
    }
  } catch (error) {
    const msg = error.response?.data?.message || '发放失败'
    ElMessage.error(msg)
    
    // 库存不足时刷新数据
    if (error.response?.data?.code === 400) {
      fetchData()
    }
  } finally {
    issuing.value = false
  }
}

// 移除原来的 mockIssueAPI 方法

// 加载数据
const fetchData = async () => {
  try {
    loading.value = true
    const params = {
      ...searchForm,
      page: pagination.page,
      limit: pagination.limit,
      store_id: userInfo.value.id,
      agent_id: userInfo.value.agent_id
    }

    const { data } = await axios.get(`${config.BASE_URL}/shop/api/coupon.php`, { params })

    if (data.code === 200) {
      tableData.value = data.data
      pagination.total = data.total
    } else {
      ElMessage.error(data.message || '加载数据失败')
    }
  } catch (error) {
    ElMessage.error('请求失败，请检查网络')
  } finally {
    loading.value = false
  }
}

// 日期格式转换
const formatDate = (dateStr) => {
  return dateStr ? dateStr.slice(0, 16).replace('T', ' ') : ''
}

// 搜索/分页处理
const handleSearch = () => {
  pagination.page = 1
  fetchData()
}

const handleSizeChange = (val) => {
  pagination.limit = val
  fetchData()
}

const handlePageChange = (val) => {
  pagination.page = val
  fetchData()
}

// 打开创建对话框
const handleCreate = () => {
  dialogType.value = 'create'
  resetForm()
  dialogVisible.value = true
}

// 打开编辑对话框
const handleEdit = (row) => {
  dialogType.value = 'edit'
  Object.assign(formData, {
    id: row.id,
    name: row.name,
    type_id: row.type_id,
    coupon_money: row.coupon_money,
    money_limit: row.money_limit,
    timeRange: [row.start_time, row.end_time],
    validRange: [row.valid_bg_time, row.valid_end_time],
    quota: row.quota,
    is_integral_exchange: row.is_integral_exchange ? 1 : 0,
    exchange_integral: row.exchange_integral,
    status: row.status
  })
  dialogVisible.value = true
}

// 表单提交
const handleSubmit = async () => {
  try {
    await formRef.value.validate()
    submitting.value = true

    const payload = {
      ...formData,
      store_id: userInfo.value.id,
      agent_id: userInfo.value.agent_id,
      start_time: formData.timeRange[0],
      end_time: formData.timeRange[1],
      valid_bg_time: formData.validRange[0],
      valid_end_time: formData.validRange[1],
    }

    const url = dialogType.value === 'create'
      ? `${config.BASE_URL}/shop/api/coupon.php`
      : `${config.BASE_URL}/shop/api/coupon.php/${formData.id}`

    const method = dialogType.value === 'create' ? 'post' : 'put'

    const { data } = await axios[method](url, payload)

    if (data.code === 200) {
      ElMessage.success('操作成功')
      dialogVisible.value = false
      fetchData()
    } else if (data.code === 400 && data.message.includes('请先配置小程序')) {
      ElMessage.error(data.message)
    } else {
      ElMessage.error(data.message || '操作失败')
    }
  } catch (error) {
    if (error.response?.status === 400) {
      ElMessage.error(error.response.data.message)
    } else {
      ElMessage.error('请求失败，请检查网络')
    }
  } finally {
    submitting.value = false
  }
}

// 删除操作
const handleDelete = async (id) => {
  try {
    await axios.delete(`${config.BASE_URL}/shop/api/coupon.php`, {
      data: {
        id: id,
        store_id: userInfo.value.id,
        agent_id: userInfo.value.agent_id
      }
    })

    ElMessage.success('删除成功')
    // 重新加载数据
    fetchData()
  } catch (error) {
    if (error.response) {
      const res = error.response.data
      if (res.code === 400) {
        ElMessage.error(res.message)
      } else if (res.code === 403) {
        ElMessage.error('无权删除该优惠券')
      } else {
        ElMessage.error('删除失败: ' + res.message)
      }
    } else {
      ElMessage.error('网络请求失败')
    }
  }
}

// 重置表单
const resetForm = () => {
  formRef.value?.resetFields()
  Object.assign(formData, {
    id: null,
    name: '',
    type_id: 1,
    coupon_money: 0,
    money_limit: 0,
    timeRange: [],
    validRange: [],
    quota: 100,
    is_integral_exchange: 0,
    exchange_integral: 0,
    status: 1
  })
}

// 初始化加载数据
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-title {
  margin-bottom: 20px;
  color: #333;
}

/* 用户选择容器 */
.user-select-container {
  .search-user {
    margin-bottom: 15px;
    max-width: 300px;
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 10px;

    .nickname {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .pagination {
    margin-top: 15px;
    display: flex;
    justify-content: flex-end;
  }
}

.search-bar {
  margin-bottom: 20px;
  background: #f5f7fa;
  padding: 20px;
  border-radius: 4px;
}

.action-bar {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.ml-10 {
  margin-left: 10px;
}

:deep(.el-date-editor) {
  width: 100%;
}

:deep(.el-input-number) {
  width: 200px;
}
</style>